import { Fragment, useEffect, useState } from "react";
import type { ComponentType } from "react";
import { useUI } from "../data/UIContext";
import { useData } from "../data/DataContext";
import {
  Close, NavDashboards, StatRunbooks, StatAlerts, NavTools,
  Activity, FileText, TrendingUp, Zap, ShoppingBag, Globe, Bug, ScrollText, Flame,
} from "./Icons";
import { api } from "../api/client";
import type {
  AnyEntity,
  BackendOrSurfaceEntity,
  IntegrationEntity,
  ClientEntity,
  ServiceCicdStats,
  ServiceDashboard,
  ActiveIncidents,
  CoverageRow,
  RecentDeploy,
  TimelineEvent,
  ClientHealth,
  SloObjective,
  SloServiceResponse,
} from "../api/types";

const GH_RUNBOOKS = "https://github.com/WaffyApp/WaffyMonitoring/blob/main";

export function SidePanel() {
  const ui = useUI();
  const data = useData();
  const item = ui.selectedEntity;

  // Lazy state for live sections
  const [recentDeploys, setRecentDeploys] = useState<RecentDeploy[] | null>(null);
  const [activeAlerts, setActiveAlerts] = useState<TimelineEvent[] | null>(null);
  const [cicdStats, setCicdStats] = useState<ServiceCicdStats | null>(null);
  const [dashboard, setDashboard] = useState<ServiceDashboard | null>(null);
  const [clientHealth, setClientHealth] = useState<ClientHealth | null>(null);
  const [slos, setSlos] = useState<SloObjective[] | null>(null);

  useEffect(() => {
    setRecentDeploys(null); setActiveAlerts(null); setCicdStats(null); setDashboard(null);
    setClientHealth(null); setSlos(null);
    if (!item) return;
    const name = encodeURIComponent(item.name);
    let cancelled = false;

    const set = <T,>(setter: (v: T) => void) => (v: T) => { if (!cancelled) setter(v); };

    // Partner clients: lazy-load per-partner health (one request, only when opened).
    if (isClientType(item) && item.type === "partner") {
      api.get<ClientHealth>(`/command-center/clients/${name}/health`)
        .then(set(setClientHealth)).catch(() => {});
      return () => { cancelled = true; };
    }

    if (!isBackendType(item)) return;

    api.get<{ deploys: RecentDeploy[] }>(`/command-center/services/${name}/recent-deploys`)
      .then(set((r) => setRecentDeploys(r.deploys ?? []))).catch(() => {});
    api.get<ActiveIncidents>(`/command-center/incidents/active`)
      .then(set((r) => setActiveAlerts((r.timeline ?? [])
        .filter((e) => e.kind === "alert" && e.service === item.name)))).catch(() => {});
    api.get<ServiceCicdStats>(`/command-center/cicd/services/${name}`)
      .then(set(setCicdStats)).catch(() => {});
    api.get<ServiceDashboard>(`/command-center/services/${name}/dashboard`)
      .then(set(setDashboard)).catch(() => {});
    api.get<SloServiceResponse>(`/command-center/slo/services/${name}`)
      .then(set((r) => setSlos(r.slos ?? []))).catch(() => {});
    return () => { cancelled = true; };
  }, [item]);

  if (!item) {
    return (
      <>
        <div className="side-panel-overlay" />
        <aside className="side-panel" aria-hidden="true" />
      </>
    );
  }

  const subtitle = [
    "type" in item ? item.type : undefined,
    "kind" in item ? item.kind : undefined,
    "tier" in item ? item.tier : undefined,
    item.owner_team,
  ].filter(Boolean).join(" · ");

  const cov = data.coverageByName[item.name];

  return (
    <>
      <div className="side-panel-overlay open" onClick={ui.closeEntity} />
      <aside className="side-panel open">
        <div className="side-header">
          <div className="side-header-text">
            <div className="side-eyebrow">{eyebrowFor(item)}</div>
            <h2 className="side-title">{item.name}</h2>
            <div className="side-subtitle">{subtitle}</div>
          </div>
          <button className="icon-btn" aria-label="Close" onClick={ui.closeEntity}>
            <Close size={16} />
          </button>
        </div>
        <div className="side-body">
          <QuickActions item={item} />

          <Section title="Details">
            <DetailsKV item={item} />
          </Section>

          {clientHealth && <PartnerHealthSection health={clientHealth} />}

          {isBackendType(item) && item.dependencies?.length > 0 && (
            <Section title="Dependencies">
              <div className="deps">
                {item.dependencies.map((d) => <span key={d} className="dep">{d}</span>)}
              </div>
            </Section>
          )}

          {isBackendType(item) && item.runbooks?.length > 0 && (
            <Section title="Runbooks">
              <ul className="list">
                {item.runbooks.map((rb) => (
                  <li key={rb}>
                    <a href={`${GH_RUNBOOKS}/${rb}`} target="_blank" rel="noopener">
                      {rb.replace(/^runbooks\//, "")}
                    </a>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {isBackendType(item) && item.known_issues?.length > 0 && (
            <Section title="Known issues">
              <ul className="list">
                {item.known_issues.map((ki) => (
                  <li key={ki.id}>
                    <code>{ki.id}</code> —{" "}
                    <a href={`${GH_RUNBOOKS}/runbooks/${ki.runbook}`} target="_blank" rel="noopener">
                      {ki.runbook}
                    </a>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {item.notes && (
            <Section title="Notes">
              <div style={{ fontSize: 13 }}>{item.notes}</div>
            </Section>
          )}

          {cov && <HealthSection cov={cov} />}

          {slos && slos.length > 0 && (
            <Section title="SLOs & error budgets">
              <ul className="list">
                {slos.map((s) => {
                  const col = s.status === "ok" ? "var(--green)"
                    : s.status === "slow-burn" ? "var(--yellow)"
                    : s.status === "no-data" ? "var(--text-faint)" : "var(--red)";
                  return (
                    <li key={s.key}>
                      <span style={{ color: col, fontWeight: 700, fontFamily: "var(--font-mono)" }}>●</span>{" "}
                      {s.slo_group} · {s.name} — target {s.objective}%
                      {s.budget_remaining_pct != null && (
                        <span style={{ color: "var(--text-muted)" }}> · {s.budget_remaining_pct}% budget left</span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </Section>
          )}

          {recentDeploys && recentDeploys.length > 0 && (
            <Section title="Recent deploys (24h)">
              <ul className="list">
                {recentDeploys.slice(0, 6).map((d, i) => (
                  <li key={`${d.job}-${d.number}-${i}`}>
                    <a href={d.url} target="_blank" rel="noopener">build #{d.number}</a>
                    {" · "}<code style={{ color: "var(--text-muted)", fontSize: 11.5 }}>{d.result ?? "unknown"}</code>
                    {" · "}<span style={{ color: "var(--text-muted)", fontSize: 11.5 }}>{new Date(d.timestamp).toLocaleString()}</span>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {activeAlerts && activeAlerts.length > 0 && (
            <Section title="Active alerts">
              <ul className="list">
                {activeAlerts.slice(0, 5).map((a, i) => (
                  <li key={i}>
                    <span className="kind-tag alert">{a.severity ?? "alert"}</span> {a.title}
                    {a.runbook && <> · <a href={a.runbook} target="_blank" rel="noopener">runbook</a></>}
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {cicdStats && cicdStats.status === "ok" && (cicdStats.deploys_30d ?? 0) > 0 && (
            <Section title="CI/CD (30d)">
              <ul className="list">
                <li>Deploys: <span className="num">{cicdStats.deploys_30d}</span> · {cicdStats.deploys_per_day}/day</li>
                <li>Success rate: <span className="num">{Math.round((cicdStats.success_rate ?? 0) * 100)}%</span></li>
                <li>Avg duration: <span className="num">
                  {cicdStats.avg_duration_ms ? Math.round(cicdStats.avg_duration_ms / 60000) + "m" : "—"}
                </span></li>
                {cicdStats.last_failure && (
                  <li>Last failure: <a href={cicdStats.last_failure.url} target="_blank" rel="noopener">
                    {cicdStats.last_failure.job} #{cicdStats.last_failure.number}
                  </a> · <span style={{ color: "var(--text-muted)", fontSize: 11.5 }}>
                    {new Date(cicdStats.last_failure.timestamp).toLocaleString()}
                  </span></li>
                )}
              </ul>
            </Section>
          )}

          {(dashboard?.kiosk_url || ("dashboard_url" in item && item.dashboard_url)) && (
            <Section title={dashboard?.title ? `Dashboard — ${dashboard.title}` : "Dashboard"}>
              <div className="dash-iframe-wrap">
                <iframe
                  src={dashboard?.kiosk_url ?? ("dashboard_url" in item ? (item.dashboard_url ?? "") : "")}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div style={{ marginTop: 6 }}>
                <a className="action-btn"
                   href={("dashboard_url" in item ? item.dashboard_url : null) ?? dashboard?.url ?? "#"}
                   target="_blank" rel="noopener">Open in Grafana ↗</a>
              </div>
            </Section>
          )}
        </div>
      </aside>
    </>
  );
}

function isBackendType(it: AnyEntity): it is BackendOrSurfaceEntity {
  return "dependencies" in it && "alerts_count" in it;
}
function isIntegrationType(it: AnyEntity): it is IntegrationEntity {
  return "kind" in it && "webhook_endpoint" in it;
}
function isClientType(it: AnyEntity): it is ClientEntity {
  return "type" in it && (it.type === "partner" || it.type === "sdk-consumer" || it.type === "end-user-cohort");
}
function eyebrowFor(it: AnyEntity): string {
  if (isBackendType(it))     return it.type === "mobile" || it.type === "nextjs" ? "Surface" : "Service";
  if (isIntegrationType(it)) return "Outbound integration";
  if (isClientType(it))      return "Inbound client";
  return "Entity";
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return <section><h3>{title}</h3>{children}</section>;
}

function DetailsKV({ item }: { item: AnyEntity }) {
  const rows: Array<[string, React.ReactNode]> = [];
  const link = (url?: string | null, txt?: string) =>
    url ? <a href={url} target="_blank" rel="noopener">{txt ?? url}</a>
        : <span style={{ color: "var(--text-muted)" }}>—</span>;

  if ("tier" in item && item.tier)               rows.push(["Tier", <span className={`tier-badge tier-${item.tier}`}>{item.tier}</span>]);
  if (item.owner_team)                            rows.push(["Owner", item.owner_team]);
  if (item.slack)                                 rows.push(["Slack", item.slack]);

  if (isBackendType(item)) {
    if (item.jira_component) rows.push(["Jira", item.jira_component]);
    if (item.namespace)      rows.push(["Namespace", item.namespace]);
    if (item.repo)           rows.push(["Repo", item.repo]);
    if (item.prom_port)      rows.push(["Prom port", item.prom_port]);
    if (item.http_port)      rows.push(["HTTP port", item.http_port]);
    if (item.dashboard_url)  rows.push(["Dashboard", link(item.dashboard_url, "open")]);
    if (item.urls) Object.entries(item.urls).forEach(([env, url]) => url && rows.push([env, link(url)]));
    if (item.bundle_id)            rows.push(["Bundle", item.bundle_id]);
    if (item.store_url)            rows.push(["Store", link(item.store_url, "open")]);
    if (item.crashlytics_project)  rows.push(["Crashlytics", item.crashlytics_project]);
    if (item.amplitude_project)    rows.push(["Amplitude", item.amplitude_project]);
    if (item.hotjar_site)          rows.push(["HotJar site", String(item.hotjar_site)]);
    if (item.sentry_project)       rows.push(["Sentry", item.sentry_project]);
    if (item.synthetic_check_url)  rows.push(["Synthetic", link(item.synthetic_check_url)]);
    rows.push(["Alerts", item.alerts_count]);
    rows.push(["SLOs", item.slos_count]);
  } else if (isIntegrationType(item)) {
    if (item.webhook_endpoint)  rows.push(["Webhook in", item.webhook_endpoint]);
    if (item.expected_freshness) rows.push(["Expected ≤", item.expected_freshness]);
    if (item.public_status_url)  rows.push(["Status page", link(item.public_status_url)]);
    if (item.dashboard_url)      rows.push(["Dashboard", link(item.dashboard_url, "open")]);
    if (item.runbook)            rows.push(["Runbook", link(`${GH_RUNBOOKS}/${item.runbook}`, item.runbook)]);
  } else if (isClientType(item)) {
    if (item.type === "partner") {
      if (item.api_key_tag)   rows.push(["API key tag", item.api_key_tag]);
      if (item.expected_qps != null) rows.push(["Expected qps", item.expected_qps]);
      if (item.contact)       rows.push(["Contact", item.contact]);
      if (item.contract_url)  rows.push(["Contract", link(item.contract_url, "open")]);
    }
    if (item.type === "sdk-consumer") {
      if (item.sdk)                   rows.push(["SDK", item.sdk]);
      if (item.min_supported_version) rows.push(["Min version", item.min_supported_version]);
    }
    if (item.type === "end-user-cohort") {
      if (item.surface) rows.push(["Surface", item.surface]);
    }
    if (item.amplitude_cohort) rows.push(["Cohort", item.amplitude_cohort]);
  }

  return (
    <dl className="kv">
      {rows.map(([k, v], i) => (<Fragment key={i}><dt>{k}</dt><dd>{v}</dd></Fragment>))}
    </dl>
  );
}

function QuickActions({ item }: { item: AnyEntity }) {
  if (isBackendType(item)) return <ServiceQuickActions item={item} />;
  if (isIntegrationType(item)) {
    return (
      <Section title="Quick actions">
        <div className="actions">
          {item.public_status_url && <ActionLink primary icon={Activity} href={item.public_status_url}>Status page</ActionLink>}
          {item.dashboard_url     && <ActionLink icon={NavDashboards} href={item.dashboard_url}>Dashboard</ActionLink>}
          {item.runbook           && <ActionLink icon={StatRunbooks} href={`${GH_RUNBOOKS}/${item.runbook}`}>Runbook</ActionLink>}
        </div>
      </Section>
    );
  }
  if (isClientType(item)) {
    return (
      <Section title="Quick actions">
        <div className="actions">
          {item.contract_url    && <ActionLink icon={FileText} href={item.contract_url}>Contract</ActionLink>}
          {item.amplitude_cohort && <ActionLink icon={TrendingUp} href="https://app.amplitude.com/analytics/gotrah/cohorts">Amplitude cohort</ActionLink>}
        </div>
      </Section>
    );
  }
  return null;
}

function ServiceQuickActions({ item }: { item: BackendOrSurfaceEntity }) {
  const data = useData();
  const ps = data.triage?.perService ?? {};
  const fill = (tpl?: string) => tpl ? tpl.replace(/{service}/g, encodeURIComponent(item.name)) : null;

  if (item.type === "mobile") {
    return (
      <Section title="Quick actions">
        <div className="actions">
          {item.crashlytics_project && (
            <ActionLink icon={Zap} href={`https://console.firebase.google.com/u/0/project/${item.crashlytics_project}/crashlytics`}>Crashlytics</ActionLink>
          )}
          {item.amplitude_project && (
            <ActionLink icon={TrendingUp} href={`https://app.amplitude.com/analytics/${item.amplitude_project}/home`}>Amplitude</ActionLink>
          )}
          {item.store_url && <ActionLink icon={ShoppingBag} href={item.store_url}>Store listing</ActionLink>}
        </div>
      </Section>
    );
  }
  if (item.type === "nextjs") {
    return (
      <Section title="Quick actions">
        <div className="actions">
          {item.urls?.prod && <ActionLink primary icon={Globe} href={item.urls.prod}>Open prod</ActionLink>}
          {item.synthetic_check_url && <ActionLink icon={Activity} href={item.synthetic_check_url}>Synthetic check</ActionLink>}
          {item.crashlytics_project && (
            <ActionLink icon={Zap} href={`https://console.firebase.google.com/u/0/project/${item.crashlytics_project}/crashlytics`}>Crashlytics</ActionLink>
          )}
          {item.amplitude_project && (
            <ActionLink icon={TrendingUp} href={`https://app.amplitude.com/analytics/${item.amplitude_project}/home`}>Amplitude</ActionLink>
          )}
          {item.sentry_project && <ActionLink icon={Bug} href={`https://sentry.io/${item.sentry_project}`}>Sentry</ActionLink>}
        </div>
      </Section>
    );
  }
  // Backend service
  return (
    <Section title="Quick actions">
      <div className="actions">
        <ActionLink icon={ScrollText} href={fill(ps.loki_by_service) ?? "#"}>Loki — all logs</ActionLink>
        <ActionLink icon={Flame} href={fill(ps.loki_errors) ?? "#"}>Loki — errors</ActionLink>
        <ActionLink icon={StatAlerts} href={fill(ps.alerting_filtered) ?? "#"}>Alerts (filtered)</ActionLink>
        <ActionLink icon={NavTools} href={fill(ps.jenkins_pipeline) ?? "#"}>Jenkins pipeline</ActionLink>
        {item.dashboard_url && <ActionLink primary icon={NavDashboards} href={item.dashboard_url}>Dashboard</ActionLink>}
      </div>
    </Section>
  );
}

function ActionLink({ href, primary, icon: Icon, children }: {
  href: string; primary?: boolean;
  icon: ComponentType<{ size?: number; className?: string }>;
  children: React.ReactNode;
}) {
  return (
    <a className={`action-btn${primary ? " primary" : ""}`}
       href={href} target="_blank" rel="noopener"><Icon size={14} />{children}</a>
  );
}

function PartnerHealthSection({ health }: { health: ClientHealth }) {
  // Real metrics, once the gateway emits the partner_key label.
  if (health.status === "ok" && (health.success_rate != null || health.requests_5m != null)) {
    const sr = health.success_rate;
    const cls = sr == null ? "score-good" : sr >= 0.99 ? "score-strong" : sr >= 0.95 ? "score-good" : sr >= 0.9 ? "score-fair" : "score-poor";
    return (
      <Section title="Partner health">
        <div className="cov-summary" style={{ marginBottom: 0 }}>
          {sr != null && (
            <span className={`score-badge ${cls}`} title="Success rate (5m)">
              <span className="score-dot" />{(sr * 100).toFixed(2)}% ok
            </span>
          )}
          {health.requests_5m != null && (
            <span className="cov-pill"><strong>{health.requests_5m}</strong> req / 5m</span>
          )}
          {health.p95_latency_ms != null && (
            <span className="cov-pill"><strong>{health.p95_latency_ms}</strong> ms p95</span>
          )}
        </div>
      </Section>
    );
  }

  // Honest non-metric states — surfaces the instrumentation gap instead of faking data.
  const msg =
    health.status === "not-instrumented"
      ? "Not yet instrumented — the API gateway doesn't emit a per-partner metric label."
      : health.status === "unavailable"
        ? `Health unavailable: ${health.reason ?? "metrics source not reachable"}.`
        : "Instrumented — metrics pending.";
  return (
    <Section title="Partner health">
      <div className="empty" style={{ padding: "16px 18px", textAlign: "start" }}>
        {msg}
        {health.next_step && (
          <div style={{ marginTop: 6, fontSize: 11.5, color: "var(--text-muted)" }}>{health.next_step}</div>
        )}
      </div>
    </Section>
  );
}

function HealthSection({ cov }: { cov: CoverageRow }) {
  const score = cov.health_score;
  const cls = score < 50 ? "score-poor" : score < 70 ? "score-fair" : score < 90 ? "score-good" : "score-strong";
  const axes: Array<[keyof CoverageRow, string]> = [
    ["has_slo", "SLO defined"],
    ["has_alert", "Alert configured"],
    ["has_runbook", "Runbook"],
    ["has_dashboard", "Dashboard"],
    ["has_owner", "Owner"],
    ["has_crashlytics", "Crashlytics (mobile)"],
    ["has_synthetic", "Synthetic check (web)"],
  ];
  return (
    <Section title="Health">
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span className={`score-badge ${cls}`} title={`Health ${score}/100`}>
          <span className="score-dot" />{score}
        </span>
        <span style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          composite of SLO, alerts, runbooks, dashboard, owner
        </span>
      </div>
      <ul className="list">
        {axes.map(([k, label]) => {
          const v = cov[k];
          if (v === "n_a" || v == null) return null;
          const sym = v === "yes" ? "✓" : v === "unknown" ? "?" : "✗";
          const col = v === "yes" ? "var(--green)" : v === "unknown" ? "var(--yellow)" : "var(--red)";
          return (
            <li key={k as string}>
              <span style={{ color: col, fontWeight: 700, fontFamily: "var(--font-mono)" }}>{sym}</span>{" "}{label}
            </li>
          );
        })}
      </ul>
    </Section>
  );
}
